import { useState } from "react";
import { Card, CardContent, Skeleton } from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { CalendarPlus, ChevronRight } from "lucide-react";
import type { AppointmentBookingPage } from "@tellescope/types-models";
import type { AppointmentBookingBlock as AppointmentBookingBlockType } from "../../../shared/schema";
import { useAppointmentBookingPages } from "../../hooks/useAppointmentBookingPages";
import { useAppointmentBookingPagesFilter } from "../../hooks/useItemFilter";
import { useIframeToken } from "../../hooks/useIframeToken";
import { AppointmentBookingDialog } from "./AppointmentBookingDialog";
import { MobileList } from "../MobileList";

interface AppointmentBookingBlockProps {
  block: AppointmentBookingBlockType;
}

interface BookingPageCardProps {
  bookingPage: AppointmentBookingPage;
  onClick: () => void;
}

function BookingPageCard({ bookingPage, onClick }: BookingPageCardProps) {
  return (
    <Card className="overflow-hidden cursor-pointer hover:bg-accent/50 transition-colors" onClick={onClick}>
      <CardContent className="px-4">
        <div className="flex gap-4">
          {/* Booking icon */}
          <div className="flex-shrink-0">
            <div className="w-14 h-14 rounded-lg flex flex-col items-center justify-center bg-primary/10">
              <CalendarPlus className="h-6 w-6 text-primary" />
            </div>
          </div>

          {/* Booking page details */}
          <div className="flex-1 min-w-0">
            <h4 className="font-medium text-sm truncate">{bookingPage.title}</h4>
          </div>

          {/* Chevron indicator */}
          <div className="flex-shrink-0 self-center">
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function BookingPagesSkeleton({ count = 3 }: { count?: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: count }).map((_, i) => (
        <Card key={i}>
          <CardContent className="px-4 py-3">
            <div className="flex gap-4">
              <Skeleton className="w-14 h-14 rounded-lg" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export function AppointmentBookingBlock({ block }: AppointmentBookingBlockProps) {
  const { t } = useTranslation("appointments");
  const { emptyMessage, maxItems = 5, itemFilter } = block.props;

  const [selectedBookingPage, setSelectedBookingPage] = useState<AppointmentBookingPage | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Hook for iframe token (disabled by default, will be triggered explicitly)
  const iframeTokenQuery = useIframeToken({ enabled: false });

  const { bookingPages: rawBookingPages, isLoading: bookingPagesLoading, error } = useAppointmentBookingPages();

  // Apply item filter if configured
  const { filteredItems: bookingPages, isLoading: filterLoading } = useAppointmentBookingPagesFilter({
    items: rawBookingPages,
    itemFilter,
  });

  const isLoading = bookingPagesLoading || filterLoading;

  // Fetch token when user clicks a booking page (event-driven, not in useEffect)
  const handleBookingPageClick = async (bookingPage: AppointmentBookingPage) => {
    setSelectedBookingPage(bookingPage);
    setDialogOpen(true);

    try {
      // Explicitly fetch iframe token (not reactive)
      await iframeTokenQuery.refetch();
    } catch (err) {
      console.error("Failed to get iframe token:", err);
    }
  };

  // Clear all dialog-related state synchronously when closing
  const handleDialogClose = (open: boolean) => {
    setDialogOpen(open);
    if (!open) {
      // Clear selection after dialog animation completes
      setTimeout(() => {
        setSelectedBookingPage(null);
      }, 200);
    }
  };

  if (isLoading) {
    return <BookingPagesSkeleton count={maxItems} />;
  }

  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="p-4 text-center text-destructive">{t("Failed to load booking pages")}</CardContent>
      </Card>
    );
  }

  return (
    <>
      <MobileList
        items={bookingPages}
        renderItem={(bookingPage) => (
          <BookingPageCard bookingPage={bookingPage} onClick={() => handleBookingPageClick(bookingPage)} />
        )}
        title={t("Book Appointment")}
        maxItems={maxItems}
        emptyMessage={emptyMessage || t("No booking options available")}
      />

      <AppointmentBookingDialog
        bookingPage={selectedBookingPage}
        open={dialogOpen}
        onOpenChange={handleDialogClose}
        iframeToken={iframeTokenQuery.token}
        isLoading={iframeTokenQuery.isLoading}
        error={iframeTokenQuery.error?.message ?? null}
      />
    </>
  );
}
